import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { format, isToday, isTomorrow } from 'date-fns';
import { CalendarEvent } from 'intermind';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface UpcomingEventsProps {
  events: CalendarEvent[];
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({ events }) => {
  const { t } = useTranslation();

  // Group events by today, tomorrow, and upcoming
  const groupEventsByDate = (events: CalendarEvent[]) => {
    const today: CalendarEvent[] = [];
    const tomorrow: CalendarEvent[] = [];
    const upcoming: CalendarEvent[] = [];
    events.forEach((event) => {
      const start = new Date(event.startTime);
      if (isToday(start)) {
        today.push(event);
      } else if (isTomorrow(start)) {
        tomorrow.push(event);
      } else {
        upcoming.push(event);
      }
    });
    return { today, tomorrow, upcoming };
  };

  const getMeetingUrl = (event: CalendarEvent): string | null => {
    const baseUrl = import.meta.env.VITE_APP_BASE_URL;

    // Check location first
    if (event.location && event.location.includes(baseUrl)) {
      return event.location;
    }

    // Then check description
    if (event.description) {
      const urlRegex = new RegExp(`${baseUrl}/[a-z]{3}-[a-z]{4}-[a-z]{3}`, 'i');
      const match = event.description.match(urlRegex);
      if (match) {
        return match[0];
      }
    }

    return null;
  };

  const { today, tomorrow, upcoming } = groupEventsByDate(events);

  const renderEventGroup = (label: string, events: CalendarEvent[], timeFormat: (date: Date) => string) =>
    events.length > 0 && (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        </div>
        <div className="flex flex-col gap-3">
          {events.map((event, idx) => {
            const meetingUrl = getMeetingUrl(event);
            return (
              <Card
                key={`${label}-${idx}`}
                className="w-full shadow-sm transition-shadow duration-200 border border-gray-100 rounded-xl overflow-hidden"
              >
                <CardContent className="flex items-center justify-between gap-2 px-4 py-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-indigo-600 min-w-[60px]">
                      {timeFormat(new Date(event.startTime))}
                    </span>
                    <span className="font-medium text-sm text-gray-900">{event.title}</span>
                  </div>
                  {meetingUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-sm font-medium text-indigo-600 border-none hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors duration-200"
                      onClick={() => (window.location.href = meetingUrl)}
                    >
                      {t('homepage.upcomingEvents.join')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className="w-full flex flex-col gap-6 p-1">
      {renderEventGroup('Today', today, (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}
      {renderEventGroup('Tomorrow', tomorrow, (date) =>
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      )}
      {renderEventGroup('Upcoming', upcoming, (date) => format(date, 'MMM d, HH:mm'))}
    </div>
  );
};

export default UpcomingEvents;
